import React, { useEffect, useState, useRef } from "react";
import {
  Button,
  Table,
  Spin,
  message,
  Row,
  Col,
  Divider,
  Space,
} from "antd";
import {
  PrinterOutlined,
  DownloadOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import API from "../../api";
import moment from "moment";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import "./InvoiceTemplateOne.css";

const InvoiceTemplateOne = () => {
  const [invoiceData, setInvoiceData] = useState(null);
  const [loading, setLoading] = useState(false);
  const printRef = useRef();
  const { saleId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Get saleId from params or location state
  const currentSaleId = saleId || location.state?.saleId;

  useEffect(() => {
    if (currentSaleId) {
      fetchInvoiceData();
    }
  }, [currentSaleId]);

  const fetchInvoiceData = async () => {
    try {
      setLoading(true);
      const response = await API.get(`/sales/${currentSaleId}`);
      console.log("✅ Invoice Data loaded:", {
        storeId: response.data?.storeId?._id,
        storeName: response.data?.storeId?.name,
        logo: response.data?.storeId?.logo,
        products: response.data?.products?.length
      });
      setInvoiceData(response.data);
    } catch (error) {
      console.error("Error fetching invoice data:", error);
      message.error("Failed to load invoice data.");
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (number) =>
    `₹${new Intl.NumberFormat("en-IN").format(number || 0)}`;

  // Calculate totals
  const calculateTotals = () => {
    if (!invoiceData) return {
      netTotal: "0.00",
      totalDiscount: "0.00",
      taxableAmount: "0.00",
      totalAmountWithoutTax: "0.00",
      totalAmount: "0.00",
    };

    let netTotal = 0;
    let totalProductDiscount = 0;
    let totalTaxableAmount = 0;
    let totalBaseAmount = 0;

    // Step 1: Calculate product-level totals (before sale-level discount)
    const productDetails = invoiceData.products.map((item) => {
      const totalWithTax = item.salePrice * item.quantity;
      const taxRate = item.taxes?.[0]?.percentage || 0;

      const baseTotal = totalWithTax / (1 + taxRate / 100);
      const taxAmount = totalWithTax - baseTotal;

      let discount = 0;
      if (item?.productDiscount?.type === "percentage") {
        discount = (item.productDiscount.value / 100) * baseTotal;
      } else if (item?.productDiscount?.type === "flat") {
        discount = (item.productDiscount?.value || 0) * item.quantity;
      }

      const baseAfterDiscount = baseTotal - discount;
      const taxAfterDiscount = (baseAfterDiscount * taxRate) / 100;

      netTotal += totalWithTax;
      totalProductDiscount += discount;
      totalTaxableAmount += taxAfterDiscount;
      totalBaseAmount += baseAfterDiscount;

      return {
        baseAfterDiscount,
        taxRate,
      };
    });

    // Step 2: Calculate sale-level discount
    let saleLevelDiscount = 0;
    if (invoiceData.discount?.value > 0) {
      if (invoiceData.discount.type === "percentage") {
        saleLevelDiscount =
          (invoiceData.discount.value / 100) * totalBaseAmount;
      } else if (invoiceData.discount.type === "flat") {
        saleLevelDiscount = Math.min(
          invoiceData.discount.value,
          totalBaseAmount
        );
      }
    }

    const totalBaseAfterSaleDiscount = Math.max(
      totalBaseAmount - saleLevelDiscount,
      0
    );

    // Step 3: Apply sale-level discount proportionally and recalculate tax for each product
    let totalTaxAfterSaleDiscount = 0;
    
    if (totalBaseAmount > 0) {
      productDetails.forEach((product) => {
        // Calculate this product's proportion of the total base
        const productProportion = product.baseAfterDiscount / totalBaseAmount;
        
        // Apply sale-level discount proportionally to this product
        const productBaseAfterSaleDiscount = totalBaseAfterSaleDiscount * productProportion;
        
        // Calculate tax on this product's amount after sale discount
        const productTax = (productBaseAfterSaleDiscount * product.taxRate) / 100;
        
        totalTaxAfterSaleDiscount += productTax;
      });
    }

    const totalAmount =
      totalBaseAfterSaleDiscount + totalTaxAfterSaleDiscount;
    const finalDiscount = totalProductDiscount + saleLevelDiscount;

    return {
      netTotal: netTotal.toFixed(2),
      totalDiscount: finalDiscount.toFixed(2),
      taxableAmount: totalTaxAfterSaleDiscount.toFixed(2),
      totalAmountWithoutTax: totalBaseAfterSaleDiscount.toFixed(2),
      totalAmount: totalAmount.toFixed(2),
    };
  };

  // Additional charges
  const deliveryCharge = parseFloat(invoiceData?.deliveryShippingCharge) || 0;
  const serviceCharge = parseFloat(invoiceData?.servicePackagingCharge) || 0;
  const otherCharge = parseFloat(invoiceData?.otherCharge) || 0;
  const totalAdditionalCharges = deliveryCharge + serviceCharge + otherCharge;

  // Get logo URL following the same logic as salesController
  const getLogoUrl = (logo) => {
    if (!logo) {
      console.log("❌ No logo provided");
      return null;
    }
    
    console.log("📷 Original logo path:", logo);
    
    // If logo starts with http or https, use it directly
    if (logo.startsWith("http://") || logo.startsWith("https://")) {
      console.log("🌐 Using HTTP logo:", logo);
      return logo;
    }
    
    // Otherwise, it's a relative path - construct full URL using API baseURL
    const baseURL = API.defaults.baseURL;
    
    // Clean up the logo path
    let cleanLogoPath = logo;
    
    // Remove leading slash if exists to avoid double slashes
    if (cleanLogoPath.startsWith("/")) {
      cleanLogoPath = cleanLogoPath.substring(1);
    }
    
    // Construct the full URL
    const fullUrl = `${baseURL}/${cleanLogoPath}`;
    console.log("🔗 Constructed logo URL:", fullUrl);
    console.log("🔗 Base URL:", baseURL);
    console.log("🔗 Logo path:", cleanLogoPath);
    
    return fullUrl;
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = async () => {
    try {
      message.loading("Generating PDF...", 0);
      
      const element = printRef.current;
      if (!element) {
        throw new Error("Invoice element not found");
      }

      // Capture the invoice container as canvas
      const canvas = await html2canvas(element, {
        scale: 2, // Higher quality
        useCORS: true, // Enable CORS for images
        allowTaint: true,
        logging: false,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = 0;

      pdf.addImage(
        imgData,
        "PNG",
        imgX,
        imgY,
        imgWidth * ratio,
        imgHeight * ratio
      );

      // Check if content exceeds one page
      if (imgHeight * ratio > pdfHeight) {
        // Multiple pages needed
        let heightLeft = imgHeight * ratio;
        let position = 0;

        while (heightLeft > 0) {
          position = heightLeft - imgHeight * ratio;
          pdf.addPage();
          pdf.addImage(
            imgData,
            "PNG",
            imgX,
            position,
            imgWidth * ratio,
            imgHeight * ratio
          );
          heightLeft -= pdfHeight;
        }
      }

      message.destroy();
      pdf.save(`Invoice_${invoiceData.invoiceId || "invoice"}.pdf`);
      message.success("Invoice Downloaded Successfully!");
    } catch (error) {
      message.destroy();
      console.error("Error generating PDF:", error);
      message.error("Failed to generate PDF. Please try printing instead.");
    }
  };

  if (!invoiceData) return null;

  const totals = calculateTotals();
  const grandTotal = parseFloat(totals.totalAmount) + totalAdditionalCharges;

  // Product columns for table
  const columns = [
    {
      title: "S.No",
      key: "index",
      width: "5%",
      align: "center",
      render: (_, __, index) => index + 1,
    },
    {
      title: "Product Description",
      key: "description",
      width: "30%",
      render: (_, product) => (
        <div>
          <div style={{ fontWeight: "300" }}>
            {product.productId?.specifications?.name ||
              product.product?.name ||
              "N/A"}
          </div>
           
          {product.tracking_number && (
            <div style={{ fontSize: "11px", color: "#666" }}>
              Chassis/Tr No: {product.tracking_number}
            </div>
          )}
          {product.batchNumber && (
            <div style={{ fontSize: "11px", color: "#666" }}>
              Batch No: {product.batchNumber}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "HSN/SAC",
      key: "hsn_sac",
      width: "10%",
      align: "center",
      render: (_, product) => product.hsn_sac || product.productId?.hsn_sac || "-",
    },
    {
      title: "Qty",
      dataIndex: "quantity",
      key: "quantity",
      width: "8%",
      align: "center",
    },
    {
      title: "Price",
      key: "salePrice",
      width: "12%",
      align: "right",
      render: (_, product) => formatNumber(product.salePrice || 0),
    },
    {
      title: "Disc.",
      key: "discount",
      width: "8%",
      align: "center",
      render: (_, product) => {
        if (!product.productDiscount) return "-";
        return `${product.productDiscount.value}${
          product.productDiscount.type === "percentage" ? "%" : "/-"
        }`;
      },
    },
    {
      title: "Tax%",
      key: "tax",
      width: "8%",
      align: "center",
      render: (_, product) => {
        const tax = product.taxes?.[0]?.percentage || 0;
        return `${tax}%`;
      },
    },
    {
      title: "Amount",
      key: "total",
      width: "12%",
      align: "right",
      render: (_, product) => {
        const total = product.salePrice * product.quantity;
        return formatNumber(total);
      },
    },
  ];

  return (
    <div className="invoice-page-wrapper">
      <div className="invoice-actions no-print">
        <Space size="middle">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => window.close()}
          >
            Close
          </Button>
          <Button
            type="primary"
            icon={<PrinterOutlined />}
            onClick={handlePrint}
          >
            Print Invoice
          </Button>
          <Button icon={<DownloadOutlined />} onClick={handleDownload}>
            Download Invoice as PDF
          </Button>
        </Space>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "50px" }}>
          <Spin size="large" />
        </div>
      ) : (
        <div className="invoice-container" ref={printRef}>
          {/* Header Section */}
          <div className="invoice-header">
            <Row gutter={24}>
              <Col span={14}>
                <div className="store-info">
                  <div className="store-logo">
                    {invoiceData.storeId?.logo ? (
                      <img
                        src={getLogoUrl(invoiceData.storeId.logo)}
                        alt="Store Logo"
                        onError={(e) => {
                          // Try fallback logo
                          const fallbackLogo = `${API.defaults.baseURL}/logo.jpeg`;
                          console.log("🔄 Trying fallback logo:", fallbackLogo);
                          if (e.target.src !== fallbackLogo) {
                            e.target.src = fallbackLogo;
                          } else {
                            e.target.style.display = "none";
                          }
                        }}
                        onLoad={(e) => {
                          console.log("✅ Logo loaded successfully:", e.target.src);
                        }}
                      />
                    ) : (
                      <div className="no-print" style={{ 
                        padding: "10px 10px", 
                        background: "#fff3cd", 
                        border: "1px solid #ffc107",
                        borderRadius: "4px",
                        fontSize: "10px",
                        color: "#856404",
                        marginBottom: "10px"
                      }}>
                        ⚠️ No logo configured for this store. Logo path: {invoiceData.storeId?.logo || 'null'}
                      </div>
                    )}
                  </div>
                  <h1 className="store-name">
                    {invoiceData.storeId?.billingHeaderName ||
                      invoiceData.storeId?.name ||
                      "Store Name"}
                  </h1>
                  <div className="store-details">
                    {invoiceData.storeId?.address && (
                      <div>{invoiceData.storeId.address}</div>
                    )}
                    <div>
                      {invoiceData.storeId?.state && (
                        <span>State: {invoiceData.storeId.state}</span>
                      )}
                      {invoiceData.storeId?.stateCode && (
                        <span style={{ marginLeft: "10px" }}>
                          State Code: {invoiceData.storeId.stateCode}
                        </span>
                      )}
                    </div>
                    {invoiceData.storeId?.pan && (
                      <div>PAN: {invoiceData.storeId.pan}</div>
                    )}
                    {invoiceData.storeId?.phone && (
                      <div>Phone: +91 {invoiceData.storeId.phone}</div>
                    )}
                    {invoiceData.storeId?.email && (
                      <div>Email: {invoiceData.storeId.email}</div>
                    )}
                    <div className="gst-info">
                      <strong>GSTIN/UIN: </strong>
                      {invoiceData.storeId?.gstNo || "-"}
                    </div>
                  </div>
                </div>
              </Col>
              <Col span={10}>
                <div className="invoice-title-section">
                  <h2 className="invoice-title">TAX INVOICE</h2>
                  <table className="invoice-info-table">
                    <tbody>
                      <tr>
                        <td className="label">Invoice No:</td>
                        <td className="value">
                          {invoiceData.invoiceId || "N/A"}
                        </td>
                      </tr>
                      <tr>
                        <td className="label">Date:</td>
                        <td className="value">
                          {invoiceData.orderDate
                            ? moment(invoiceData.orderDate).format("DD-MM-YYYY")
                            : "N/A"}
                        </td>
                      </tr>
                      {/* <tr>
                        <td className="label">Status:</td>
                        <td className="value">
                          <span
                            className={`status-badge status-${invoiceData.status}`}
                          >
                            {invoiceData.status?.toUpperCase()}
                          </span>
                        </td>
                      </tr> */}
                    </tbody>
                  </table>
                </div>
              </Col>
            </Row>
          </div>

          {/* <Divider style={{ margin: "10px 0", borderColor: "#000" }} /> */}

          {/* Customer and Finance Section */}
          <Row gutter={24} style={{ marginBottom: "10px" }}>
            <Col span={12}>
              <div className="info-box">
                <div className="info-box-header">Bill To</div>
                <div className="info-box-content">
                  <div className="info-row">
                    <span className="info-label">Name:</span>
                    <span className="info-value">
                      {invoiceData.customerId?.name || "N/A"}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Phone:</span>
                    <span className="info-value">
                      {invoiceData.customerId?.phone || "N/A"}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Address:</span>
                    <span className="info-value">
                      {invoiceData.customerId?.address
                        ? `${invoiceData.customerId.address.street || ""}, ${
                            invoiceData.customerId.address.city || ""
                          }, ${invoiceData.customerId.address.state || ""}, ${
                            invoiceData.customerId.address.pincode || ""
                          }`
                        : "N/A"}
                    </span>
                  </div>
                  {invoiceData.customerId?.gstNumber && (
                    <div className="info-row">
                      <span className="info-label">GSTIN:</span>
                      <span className="info-value">
                        {invoiceData.customerId.gstNumber}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </Col>

            <Col span={12}>
              {/* {invoiceData.employeeId?.name && (
                <div className="info-box">
                  <div className="info-box-header">Sold By</div>
                  <div className="info-box-content">
                    <div className="info-row">
                      <span className="info-label">Employee:</span>
                      <span className="info-value">
                        {invoiceData.employeeId.name}
                      </span>
                    </div>
                    {invoiceData.employeeId.phone && (
                      <div className="info-row">
                        <span className="info-label">Phone:</span>
                        <span className="info-value">
                          {invoiceData.employeeId.phone}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )} */}

              {(invoiceData.finance ||
                (invoiceData.productFinanceDetails &&
                  invoiceData.productFinanceDetails.length > 0)) && (
                <div className="info-box">
                  <div className="info-box-header">Finance Details</div>
                  <div className="info-box-content">
                    {invoiceData.finance && (
                      <div className="info-row">
                        <span className="info-label">Financer:</span>
                        <span className="info-value">
                          {invoiceData.finance
                            .replace(/([a-z])([A-Z])/g, "$1 $2")
                            .split(" ")
                            .map(
                              (word) =>
                                word.charAt(0).toUpperCase() +
                                word.slice(1).toLowerCase()
                            )
                            .join(" ")}
                        </span>
                      </div>
                    )}
                    {invoiceData.productFinanceDetails &&
                      invoiceData.productFinanceDetails.map((detail, index) => (
                        <div className="info-row" key={index}>
                          <span className="info-label">{detail.key}:</span>
                          <span className="info-value">
                            {detail.value || "N/A"}
                          </span>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </Col>
          </Row>

          {/* Products Table */}
          <div className="products-table-section">
            <Table
              columns={columns}
              dataSource={invoiceData.products || []}
              rowKey={(record, index) => index}
              pagination={false}
              bordered
              size="small"
              className="products-table"
            />
          </div>

          {/* Totals Section */}
          <Row gutter={24} style={{ marginTop: "10px" }}>
            <Col span={12}>
              <div className="payment-details">
                <h3 className="section-title">Payment Details</h3>
                
                {/* Show payment method with amounts */}
                {invoiceData.paymentMethod === "cash_online" ? (
                  <>
                    <div className="detail-row">
                      <span className="detail-label">Cash:</span>
                      <span className="detail-value payment-method">
                        {formatNumber(invoiceData.cash || 0)}
                      </span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Online/UPI:</span>
                      <span className="detail-value payment-method">
                        {formatNumber(invoiceData.online || 0)}
                      </span>
                    </div>
                  </>
                ) : (
                  <div className="detail-row">
                    <span className="detail-label">
                      {invoiceData.paymentMethod?.toUpperCase() || "Payment"}:
                    </span>
                    <span className="detail-value payment-method">
                      {formatNumber(invoiceData.paidAmount || 0)}
                    </span>
                  </div>
                )}

                <div className="detail-row">
                  <span className="detail-label">Total Paid:</span>
                  <span className="detail-value">
                    {formatNumber(invoiceData.paidAmount || 0)}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Balance Amount:</span>
                  <span className="detail-value balance-amount">
                    {formatNumber(invoiceData.balanceAmount || 0)}
                  </span>
                </div>

                {invoiceData.discount?.value > 0 && (
                  <>
                    <Divider style={{ margin: "5px 0" }} />
                    <div className="detail-row">
                      <span className="detail-label">Sale Discount:</span>
                      <span className="detail-value">
                        {invoiceData.discount.value}{" "}
                        {invoiceData.discount.type === "percentage" ? "%" : "/-"}
                      </span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Total Discount:</span>
                      <span className="detail-value">
                        {formatNumber(totals.totalDiscount)}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </Col>

            <Col span={12}>
              <div className="amount-summary">
                <h3 className="section-title">Amount Summary</h3>

                <div className="summary-row">
                  <span className="summary-label">Taxable Amount:</span>
                  <span className="summary-value">
                    {formatNumber(totals.totalAmountWithoutTax)}
                  </span>
                </div>

                
                <div className="summary-row">
                  <span className="summary-label">GST (SGST+CGST):</span>
                  <span className="summary-value">
                    {formatNumber(totals.taxableAmount)}
                  </span>
                </div>

                <div className="summary-row subtotal-row">
                  <span className="summary-label">Subtotal (with Tax):</span>
                  <span className="summary-value">
                    {formatNumber(totals.netTotal)}
                  </span>
                </div>

                {totalAdditionalCharges > 0 && (
                  <>
                    {deliveryCharge > 0 && (
                      <div className="summary-row">
                        <span className="summary-label">
                          Delivery/Shipping Charge:
                        </span>
                        <span className="summary-value">
                          {formatNumber(deliveryCharge)}
                        </span>
                      </div>
                    )}
                    {serviceCharge > 0 && (
                      <div className="summary-row">
                        <span className="summary-label">
                          Service/Packaging Charge:
                        </span>
                        <span className="summary-value">
                          {formatNumber(serviceCharge)}
                        </span>
                      </div>
                    )}
                    {otherCharge > 0 && (
                      <div className="summary-row">
                        <span className="summary-label">Other Charges:</span>
                        <span className="summary-value">
                          {formatNumber(otherCharge)}
                        </span>
                      </div>
                    )}
                  </>
                )}
                
                <Divider style={{ margin: "5px 0", borderColor: "#000" }} />
                <div className="summary-row grand-total">
                  <span className="summary-label">Grand Total:</span>
                  <span className="summary-value">
                    {formatNumber(grandTotal)}
                  </span>
                </div>
              </div>
            </Col>
          </Row>

          {/* Terms and Conditions */}
          <div className="terms-section">
            <h3 className="terms-title">Terms and Conditions:</h3>
            <ol className="terms-list">
              <li>
                <strong>No Product Returns or Money Back:</strong> All sales are
                final. No product returns or refunds will be entertained under
                any circumstances.
              </li>
              <li>
                <strong>Exchange Policy:</strong>
                <ul>
                  <li>
                    Products can be exchanged within 3 days of purchase.
                  </li>
                  <li>
                    The original sale invoice must be provided at the time of
                    exchange.
                  </li>
                  <li>
                    Exchange requests without the sale invoice will not be
                    processed.
                  </li>
                </ul>
              </li>
              <li>
                <strong>Check Products at the Counter:</strong> Customers are
                requested to thoroughly inspect the product(s) at the counter
                before leaving the store.
              </li>
              <li>
                <strong>Damaged, Broken and Used Products:</strong> Products
                that are damaged, broken or used after purchase will not be
                eligible for exchange or return.
              </li>
            </ol>
          </div>

          {/* Footer */}
          <div className="invoice-footer">
            <Row>
              <Col span={12}>
                <div className="footer-left">
                  <div className="contact-info">
                    For any queries, please contact:
                  </div>
                  <div>Phone: +91 {invoiceData.storeId?.phone || "N/A"}</div>
                  <div>
                    Email: {invoiceData.storeId?.email || "rhinotraders14@gmail.com"}
                  </div>
                </div>
              </Col>
              <Col span={12}>
                <div className="footer-right">
                  <div className="signature-section">
                    <div className="signature-line"></div>
                    <div className="signature-label">Authorized Signature</div>
                  </div>
                </div>
              </Col>
            </Row>
            <div className="company-name">
              {invoiceData.storeId?.billingHeaderName || ""}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceTemplateOne;

